import React, { useState, useMemo, useRef, useEffect } from "react";
import { Slider, IconButton, Box } from "@mui/material";
import { gsap } from "gsap";
import Helper from "../functions/Helper.js";
import { MotionPathPlugin } from "gsap/dist/MotionPathPlugin.js";
import {
  createAnimationSteps,
  drawPhase,
  initialStateOfArrows,
} from "../functions/AnimationFunctions.js";
import AnimationTimelineSettings from "./AnimationTimelineSettings.js";
import { useLocalStorage } from "usehooks-ts";
import ActionTimeline from "./ActionTimeline.js";
import { createPortal } from "react-dom";
import Iconify from "../../components/Iconify.js";
import useCurrentValue from "../../hooks/useCurrentValue.js";
import useDeepCompareEffect from "use-deep-compare-effect";
import { createTheme, ThemeProvider } from "@mui/material/styles";

gsap.registerPlugin(MotionPathPlugin);

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});

export default function AnimationTimeline(props) {
  const [timestamps, setTimestamps] = useState([]);
  const currentTimeline = useRef();
  const { actionTimeLineRef = false, mirror, setMirror } = props;

  const phaseWithoutStatus = useMemo(() => {
    return {
      ...props.phase,
      objects: props.phase.objects.map((obj) => {
        const { status, ...rest } = obj;
        return rest;
      }),
    };
  }, [props.phase]);

  const [steps, setSteps] = useState(
    Helper.calculateNextPhase(props.phase, props.editor.court).steps
  );

  const showPlayButton = steps.some((step) => step.changes);

  useDeepCompareEffect(() => {
    setSteps(Helper.calculateNextPhase(props.phase, props.editor.court).steps);
  }, [phaseWithoutStatus, props.editor.court]);

  const animationSteps = useMemo(() => createAnimationSteps(steps), [steps]);
  const [seekPos, setSeekPos] = useState(0);
  const [paused, setPaused] = useState(true);
  const [speed, setSpeed] = useLocalStorage("thg_speed", 1);
  const [recalculateTimeline, setRecalculateTimeline] = useState(false);
  const inAnimator = props.inAnimator ?? false;
  const pausedRef = useCurrentValue(paused);
  const seekPosRef = useCurrentValue(seekPos);

  useEffect(() => {
    if (props.mode !== "fullscreen") return;
    function handleKeyDown(e) {
      // check for space button
      if (e.keyCode === 32) {
        if (pausedRef.current) {
          if (seekPosRef.current > 9999) {
            setSeekPos(0);
            currentTimeline.current.seek(0);
          }
          currentTimeline.current.timeScale(speed).resume();
          setPaused(false);
        } else {
          currentTimeline.current.pause();
          setPaused(true);
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    currentTimeline.current?.timeScale(speed);
  }, [speed]);

  const createTimeline = (prefix, debug = false) => {
    let timestamps = [];

    var timeline = gsap.timeline({
      paused: true,

      onUpdate: (x) => {
        setSeekPos(timeline.progress() * 10000);
      },
      onComplete: () => {
        setPaused(true);
      },
    });

    if (debug) {
      var debugLog = [];
      const debugTimelineTo = (node, object, when) => {
        debugLog.push({
          node,
          duration: object.duration,
          object,
          when,
        });
      };

      timeline.to = debugTimelineTo;
    }

    initialStateOfArrows(steps, timeline, prefix);

    drawPhase(
      timeline,
      animationSteps,
      () => prefix,
      timestamps,
      props.editor.court,
      inAnimator
    );

    if (debug) {
      console.log("debugLog", debugLog);
    }
    return { timeline, timestamps };
  };

  useEffect(() => {
    currentTimeline.current?.pause();
    currentTimeline.current?.seek(0);
    setRecalculateTimeline(true);
    //createTimeline(props.prefix, false);
  }, [steps, props.editor.court]);

  useEffect(() => {
    if (recalculateTimeline) {
      let tempData = createTimeline(props.prefix);
      setTimestamps(tempData.timestamps);
      currentTimeline.current = tempData.timeline;

      setSeekPos(0);
      setPaused(true);
      setRecalculateTimeline(false);
    }
  }, [recalculateTimeline]);

  const goToTimestamp = (index) => {
    currentTimeline.current.pause();
    currentTimeline.current.seek(timestamps[index].arrowFullyDrawn);
    setSeekPos(
      (timestamps[index].arrowFullyDrawn / currentTimeline.current.duration()) *
        10000
    );
  };

  const iconFontSize = props.mode === "fullscreen" ? 30 : 20;

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        className="timeline_box"
        sx={{
          position: "absolute",
          bottom: 7,
          left: 0,
          right: 0,
          height: "45px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          background: `linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.4))`,
          opacity: 0,
          visibility: "hidden",
          transition: "opacity 0.5s, visibility 0.2s",
        }}
      >
        <Box sx={{ pb: 0.5, px: 0.5 }}>
          <Box className="animation_timeline">
            {actionTimeLineRef &&
              actionTimeLineRef.current &&
              createPortal(
                <ActionTimeline steps={steps} goToTimestamp={goToTimestamp} />,
                actionTimeLineRef.current
              )}
            {showPlayButton && (
              <Box
                sx={{
                  flex: 1,
                  px: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              ></Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mx: props.mode === "fullscreen" ? 2 : 0,
              }}
            >
              {showPlayButton && (
                <Box>
                  {paused ? (
                    <IconButton
                      sx={{
                        "&:focus": {
                          backgroundColor: "unset !important",
                          borderColor: "unset!important",
                        },
                        fontSize: 22,
                      }}
                      id={props.prefix + "_button_play"}
                      size={props.mode === "fullscreen" ? "large" : "small"}
                      onClick={() => {
                        if (seekPos > 9999) {
                          setSeekPos(0);
                          currentTimeline.current.seek(0);
                        }
                        currentTimeline.current.timeScale(speed).resume();
                        setPaused(false);
                      }}
                      aria-label="play"
                    >
                      <Iconify icon={"iconoir:play-solid"} />
                    </IconButton>
                  ) : (
                    <IconButton
                      sx={{
                        "&:focus": {
                          backgroundColor: "unset !important",
                          borderColor: "unset!important",
                        },
                        fontSize: 22,
                      }}
                      size={props.mode === "fullscreen" ? "large" : "small"}
                      onClick={() => {
                        currentTimeline.current.pause();
                        setPaused(true);
                      }}
                      aria-label="delete"
                    >
                      <Iconify icon={"iconoir:pause-solid"} />
                    </IconButton>
                  )}
                </Box>
              )}
              {showPlayButton && (
                <Slider
                  step={100}
                  id={props.prefix + "_play"}
                  aria-label="time-indicator"
                  size="small"
                  value={seekPos}
                  min={0}
                  max={10000}
                  onChange={(_, value) => {
                    currentTimeline.current.pause();
                    setPaused(true);
                    setSeekPos(value);
                    currentTimeline.current.seek(
                      (value / 10000) * currentTimeline.current.duration()
                    );
                  }}
                  sx={(theme) => {
                    return {
                      "@media (pointer: coarse)": {
                        padding: "10px 0px",
                      },

                      "&:hover , &:active": {
                        "& .MuiSlider-thumb": {
                          display: "block",
                        },
                      },
                      padding: "10px 0px",
                      alignItems: "center",
                      transition: "none",
                      color: "#fff",
                      height: 3,
                      ml: 1,
                      mr: 2,
                      "& .MuiSlider-thumb.Mui-active": {
                        boxShadow: "0px 0px 0px 8px rgba(0, 150, 136, 0.16)",
                      },

                      "& .MuiSlider-thumb": {
                        display: "none",
                        width: 8,
                        height: 8,
                        transition: "none",
                        color: "#ffff",

                        "&.Mui-hover": {
                          boxShadow: "none",
                        },
                      },
                      "& .MuiSlider-rail": {
                        transition: "none",
                        opacity: 0.15,
                      },
                      "& .MuiSlider-track": {
                        transition: "none",
                      },
                    };
                  }}
                />
              )}

              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {showPlayButton && (
                  <Box>
                    <AnimationTimelineSettings
                      iconSize={props.mode === "fullscreen" ? "large" : "small"}
                      iconFontSize={iconFontSize}
                      containerRef={props.containerRef}
                      setSpeed={setSpeed}
                      speed={speed}
                      mode={props.mode}
                    />
                  </Box>
                )}

                {setMirror && (
                  <IconButton
                    size={props.mode === "fullscreen" ? "large" : "small"}
                    onClick={() => setMirror(!mirror)}
                    sx={(theme) => ({
                      "&:focus": {
                        backgroundColor: "unset !important",
                        borderColor: "unset!important",
                      },
                      color: mirror
                        ? theme.palette.error.main + "!important"
                        : "#fff",
                      fontSize: iconFontSize,
                      ml: 1,
                      mr: -1,
                    })}
                  >
                    <Iconify icon={"solar:flip-horizontal-bold"} />
                  </IconButton>
                )}

                {props.requestFullscreen && (
                  <IconButton
                    size={props.mode === "fullscreen" ? "large" : "small"}
                    onClick={() => {
                      props.requestFullscreen();
                    }}
                    sx={(theme) => ({
                      "&:focus": {
                        backgroundColor: "unset !important",
                        borderColor: "unset!important",
                      },
                      color: "#fff",
                      fontSize: iconFontSize,
                      ml: 1,
                    })}
                  >
                    <Iconify icon={"mingcute:fullscreen-line"} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
